import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { InvoiceService } from 'api';
import { isSuccessful } from 'api/utils';
import { StyledButton } from 'components/Buttons';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { CorrectiveInvoice, Invoice } from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { InvoiceTab } from 'root/pathnames';
import { appToast } from 'utils/toast.utils';
import { TableData } from '../../../../invoicesTable.types';
import * as S from '../../InvoiceTableActions.css';

type Props = {
  open: boolean;
  selectedInvoices:
    | TableData[]
    | (Invoice & { isCorrective: boolean })[]
    | (CorrectiveInvoice & { isCorrective: boolean })[];
  onClose: () => void;
  onSubmit: (() => Promise<void>) | (() => void);
};

export const ConfirmCancelDialog = ({
  open,
  selectedInvoices,
  onClose,
  onSubmit,
}: Props) => {
  const { tab: invoiceTab } = useParams<{
    tab: InvoiceTab;
  }>();

  const { t } = useTranslation(['misc']);

  const { loading, cancelInvoice } = InvoiceService.useCancelInvoice();

  const handleSubmit = async () => {
    const responses = await Promise.all(
      selectedInvoices.map((i) => cancelInvoice(i.id, i.isCorrective)),
    );
    responses.every((res) => isSuccessful(res)) &&
      appToast.success(t('message.deleteSuccess', { ns: 'misc' }));
    onClose();
    await onSubmit();
  };

  return (
    <S.StyledDialog open={open} onClose={onClose}>
      <LoaderFullScreen loading={loading} />
      <DialogTitle>
        {invoiceTab === InvoiceTab.FOREIGN
          ? t('modal.deleteEntityHeading', { ns: 'misc' })
          : t('cancelInvoiceHeading', { ns: 'invoice' })}
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText>
          {invoiceTab === InvoiceTab.FOREIGN
            ? t('modal.deleteEntityBody', { ns: 'misc' })
            : t('cancelInvoiceBody', { ns: 'invoice' })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <StyledButton $primary onClick={handleSubmit}>
          {invoiceTab === InvoiceTab.FOREIGN
            ? t('button.delete', { ns: 'misc' })
            : t('cancelInvoiceButton', { ns: 'invoice' })}
        </StyledButton>
        <StyledButton onClick={onClose}>
          {t('button.cancel', { ns: 'misc' })}
        </StyledButton>
      </DialogActions>
    </S.StyledDialog>
  );
};
